/**
 * @21k/shared — single source of truth for enums and cross-cutting
 * interfaces used by both backend and frontend.
 *
 * Always add new enums here before referencing them in either app.
 * Enum names and values MUST stay in lock-step with `prisma/schema.prisma`.
 */

// ============================================================================
// Auth / RBAC
// ============================================================================

export enum Role {
  /** Bypasses all permission checks. Can elevate others to SUPER_ADMIN. */
  SUPER_ADMIN = 'SUPER_ADMIN',
  /** All module access comes from SystemRole assignments. */
  USER = 'USER',
}

/** Agent availability for ticket assignment and staff UI. */
export enum AgentStatus {
  LIVE = 'LIVE',
  AWAY = 'AWAY',
  ON_LEAVE = 'ON_LEAVE',
  OFFLINE = 'OFFLINE',
}

/** Modules that can have permissions assigned via SystemRoles. */
export enum SystemModule {
  ROLE_MANAGEMENT = 'ROLE_MANAGEMENT',
  STAFF_MANAGEMENT = 'STAFF_MANAGEMENT',
  DEPARTMENT_MANAGEMENT = 'DEPARTMENT_MANAGEMENT',
  PLATFORM_CONFIG = 'PLATFORM_CONFIG',
  SLA_MANAGEMENT = 'SLA_MANAGEMENT',
  TICKET_MANAGEMENT = 'TICKET_MANAGEMENT',
  REPORTS = 'REPORTS',
}

/** Modules that support data export — show export permission in role editor. */
export const MODULES_WITH_EXPORT: SystemModule[] = [
  SystemModule.STAFF_MANAGEMENT,
  SystemModule.TICKET_MANAGEMENT,
  SystemModule.REPORTS,
];

export const ALL_SYSTEM_MODULES: SystemModule[] = Object.values(SystemModule);

export type ModuleAction = 'read' | 'write' | 'edit' | 'export';

export interface ModulePermissionFlags {
  canRead: boolean;
  canWrite: boolean;
  canEdit: boolean;
  canExport: boolean;
}

export interface ModulePermissionEntry extends ModulePermissionFlags {
  module: SystemModule;
}

/** Human-readable labels for admin UI. */
export const SYSTEM_MODULE_LABELS: Record<SystemModule, string> = {
  [SystemModule.ROLE_MANAGEMENT]: 'Role Management',
  [SystemModule.STAFF_MANAGEMENT]: 'Staff Management',
  [SystemModule.DEPARTMENT_MANAGEMENT]: 'Department Management',
  [SystemModule.PLATFORM_CONFIG]: 'Platform Config',
  [SystemModule.SLA_MANAGEMENT]: 'SLA Management',
  [SystemModule.TICKET_MANAGEMENT]: 'Ticket Management',
  [SystemModule.REPORTS]: 'Reports',
};

// ============================================================================
// Departments
// ============================================================================

export enum DepartmentType {
  SCHOOL = 'SCHOOL',
  PROGRAM = 'PROGRAM',
  INTERFACE = 'INTERFACE',
  INTERNAL = 'INTERNAL',
}

export enum DepartmentStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum DepartmentRole {
  DEPARTMENT_ADMIN = 'DEPARTMENT_ADMIN',
  AGENT = 'AGENT',
}

// ============================================================================
// Channels
// ============================================================================

/** How agent replies are routed — fixed enum (integration behaviour). */
export enum ChannelReplyMode {
  EMAIL = 'EMAIL',
  WHATSAPP = 'WHATSAPP',
  PARENT_NOTIFY = 'PARENT_NOTIFY',
  INTERNAL_IN_APP = 'INTERNAL_IN_APP',
  NONE = 'NONE',
}

export const CHANNEL_REPLY_MODE_LABELS: Record<ChannelReplyMode, string> = {
  [ChannelReplyMode.EMAIL]: 'Email thread',
  [ChannelReplyMode.WHATSAPP]: 'WhatsApp (WATI)',
  [ChannelReplyMode.PARENT_NOTIFY]: 'Parent notify (in-app + email)',
  [ChannelReplyMode.INTERNAL_IN_APP]: 'Internal in-app only',
  [ChannelReplyMode.NONE]: 'No outbound reply',
};

export enum CommunicationMode {
  LIVE_CHAT = 'LIVE_CHAT',
  WHATSAPP_CONVERSATION = 'WHATSAPP_CONVERSATION',
  EMAIL_THREAD = 'EMAIL_THREAD',
  SMS_THREAD = 'SMS_THREAD',
  COMMENTS_ONLY = 'COMMENTS_ONLY',
}

export const COMMUNICATION_MODE_LABELS: Record<CommunicationMode, string> = {
  [CommunicationMode.LIVE_CHAT]: 'Live chat',
  [CommunicationMode.WHATSAPP_CONVERSATION]: 'WhatsApp',
  [CommunicationMode.EMAIL_THREAD]: 'Email',
  [CommunicationMode.SMS_THREAD]: 'SMS',
  [CommunicationMode.COMMENTS_ONLY]: 'Internal',
};

export enum MessageVisibility {
  CUSTOMER_VISIBLE = 'CUSTOMER_VISIBLE',
  INTERNAL_NOTE = 'INTERNAL_NOTE',
  SYSTEM_EVENT = 'SYSTEM_EVENT',
}

/**
 * Agent "reply to parent" (CUSTOMER_VISIBLE) is off while OTS is used internally.
 * Flip to true to restore the composer toggle and outbound parent replies.
 */
export const AGENT_PARENT_REPLIES_ENABLED = false;

export enum MessageAuthorType {
  AGENT = 'AGENT',
  PARENT = 'PARENT',
  SYSTEM = 'SYSTEM',
}

export enum PreferredContact {
  EMAIL = 'EMAIL',
  WHATSAPP = 'WHATSAPP',
  SMS = 'SMS',
}

// ============================================================================
// Tickets
// ============================================================================

/** Lifecycle side-effect when entering a status (fixed — drives ticket fields). */
export enum TicketStatusBehavior {
  NONE = 'NONE',
  COMPLETE = 'COMPLETE',
  CANNOT_RESOLVE = 'CANNOT_RESOLVE',
  REQUEST_CONFIRMATION = 'REQUEST_CONFIRMATION',
}

export const TICKET_STATUS_BEHAVIOR_LABELS: Record<TicketStatusBehavior, string> = {
  [TicketStatusBehavior.NONE]: 'No special behavior',
  [TicketStatusBehavior.COMPLETE]: 'Mark ticket completed',
  [TicketStatusBehavior.CANNOT_RESOLVE]: 'Mark cannot resolve',
  [TicketStatusBehavior.REQUEST_CONFIRMATION]: 'Request close confirmation',
};

export enum TicketPriority {
  NONE = 'NONE',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

/** Customer-facing case vs internal staff-only case — set at creation, never changes. */
export enum TicketAudience {
  CUSTOMER = 'CUSTOMER',
  INTERNAL = 'INTERNAL',
}

export const TICKET_AUDIENCE_LABELS: Record<TicketAudience, string> = {
  [TicketAudience.CUSTOMER]: 'Customer',
  [TicketAudience.INTERNAL]: 'Internal',
};

// ============================================================================
// API contracts
// ============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ApiError {
  error: string;
  code?: string;
  details?: unknown;
}

// ============================================================================
// Linked profile / enrollment lookup
// ============================================================================

export type {
  EnrollmentSourceRule,
  LinkedEntitySource,
} from './enrollment-source-rules';

export {
  DEFAULT_ENROLLMENT_SOURCE_RULES,
  ENROLLMENT_NUMBER_FORMAT_HINT,
  formatEnrollmentSourceHint,
  inferEnrollmentSource,
  inferEnrollmentSourceFromRules,
  parseEnrollmentSourceRules,
} from './enrollment-source-rules';

export {
  ALLOWED_ATTACHMENT_EXTENSIONS_LABEL,
  ALLOWED_ATTACHMENT_MIME_TYPES,
  isAllowedAttachmentMimeType,
  MAX_ATTACHMENT_BATCH_BYTES,
  MAX_ATTACHMENT_BATCH_MB,
  validateAttachmentBatch,
} from './attachments';

// ============================================================================
// Socket.IO — real-time push (HTTP remains the write path)
// ============================================================================

export interface SocketAsset {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface SocketComment {
  id: string;
  ticketId: string;
  content: string;
  visibility: 'CUSTOMER_VISIBLE' | 'INTERNAL_NOTE' | 'SYSTEM_EVENT';
  authorType: 'AGENT' | 'PARENT' | 'SYSTEM';
  authorId: string | null;
  parentAuthorName: string | null;
  senderName: string;
  isFromParent: boolean;
  isSystemEvent: boolean;
  sentViaChannel: boolean;
  assets: SocketAsset[];
  createdAt: string;
}

export interface TicketSocketUpdate {
  ticketId: string;
  statusCode?: string;
  statusLabel?: string;
  assignedToName?: string;
  communicationMode?: string;
}

/** Full ticket row pushed to dept / platform rooms for live list sync. */
export interface TicketListSocketItem {
  id: string;
  ticketNumber: string;
  departmentId: string;
  title: string;
  description: string;
  statusId: string;
  status: {
    id: string;
    code: string;
    name: string;
    isTerminal: boolean;
    onEnterBehavior: string;
  };
  labels: Array<{ id: string; code: string; name: string; color: string | null }>;
  categoryId: string;
  category: { id: string; code: string; name: string };
  priority: TicketPriority;
  audience: TicketAudience;
  parentFacingDepartmentId: string | null;
  channelTypeId: string;
  channelType: { id: string; code: string; name: string; replyMode?: string };
  linkedParentId: string | null;
  linkedLearnerId: string | null;
  linkedLearnerName: string | null;
  linkedEnrollmentId?: string | null;
  linkedSource: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  communicationMode: string;
  preferredContact: string | null;
  slaDeadline: string | null;
  slaBreach: boolean;
  createdById: string | null;
  assignedToId: string | null;
  createdAt: string;
  updatedAt: string;
  department: { id: string; name: string; type: string };
  parentFacingDepartment?: { id: string; name: string; type: string } | null;
  createdBy: { id: string; firstName: string; lastName: string; email: string } | null;
  assignedTo: { id: string; firstName: string; lastName: string; email: string } | null;
}

export interface TicketListSocketPayload {
  ticket: TicketListSocketItem;
}

export type TicketUpdatedSocketPayload = TicketListSocketPayload | TicketSocketUpdate;

export function isTicketListSocketPayload(
  payload: TicketUpdatedSocketPayload,
): payload is TicketListSocketPayload {
  return 'ticket' in payload;
}

export interface SocketNotification {
  id: string;
  agentId: string;
  type: 'TICKET_ASSIGNED' | 'NEW_MESSAGE' | 'INTERNAL_NOTE' | 'STATUS_CHANGED';
  title: string;
  body: string;
  ticketId: string | null;
  read: boolean;
  createdAt: string;
  ticket: {
    id: string;
    ticketNumber: string;
    title: string;
  } | null;
}

export interface AgentStatusChangedPayload {
  agentId: string;
  status: AgentStatus;
}

export interface ServerToClientEvents {
  'message:new': (comment: SocketComment) => void;
  'ticket:new': (payload: TicketListSocketPayload) => void;
  'ticket:updated': (payload: TicketUpdatedSocketPayload) => void;
  'notification:new': (notification: SocketNotification) => void;
  'agent:status_changed': (data: AgentStatusChangedPayload) => void;
  'user:typing': (data: { userId: string; name: string; ticketId: string }) => void;
  'user:stopped_typing': (data: { userId: string; ticketId: string }) => void;
}

export interface ClientToServerEvents {
  'join:ticket': (ticketId: string, callback: (err?: string) => void) => void;
  'leave:ticket': (ticketId: string) => void;
  'typing:start': (ticketId: string) => void;
  'typing:stop': (ticketId: string) => void;
}

export interface InterServerEvents {}

export interface SocketData {
  userId: string | null;
  widgetParentId: string | null;
  widgetSource: string | null;
  displayName: string;
  isAgent: boolean;
  joinedTicketRooms: Set<string>;
}
